import type {
  ExcelChatMessage,
  ExcelPausedPlanPayload,
  ExcelStepStatus,
  ExecutorRunCheckpoint,
  WorkbookModification,
} from "./types";
import type { ExcelHardErrorCell } from "./excelHardErrors";

export function retargetExcelCheckpointActions(
  messages: readonly ExcelChatMessage[],
  sourceWorkbookKey: string,
  targetWorkbookKey: string,
  targetWorkbookName: string,
): ExcelChatMessage[] {
  const normalizedSourceKey = sourceWorkbookKey.trim().toLowerCase();
  return messages.map((message) => {
    let changed = false;
    const actions = message.actions?.map((action) => {
      const actionWorkbookKey = action.pausedPlanPayload?.workbookKey;
      if (
        typeof actionWorkbookKey !== "string" ||
        actionWorkbookKey.trim().toLowerCase() !==
        normalizedSourceKey
      ) {
        return action;
      }
      changed = true;
      return {
        ...action,
        pausedPlanPayload: {
          ...action.pausedPlanPayload,
          workbookKey: targetWorkbookKey,
          workbookName: targetWorkbookName,
        },
      };
    });
    return changed ? { ...message, actions } : message;
  });
}

export function consumeExcelCheckpointAction(
  messages: readonly ExcelChatMessage[],
  messageId: string,
  actionId: string,
): ExcelChatMessage[] {
  const actionExists = messages.some(
    (message) =>
      message.id === messageId &&
      message.actions?.some((action) => action.id === actionId),
  );
  if (!actionExists) return messages.slice();
  return invalidateExcelCheckpointActions(messages, { messageId, actionId });
}

/**
 * Drops one continuation action and nothing else.
 *
 * `consumeExcelCheckpointAction` supersedes every paused-plan action because
 * the run it starts takes over all the remaining work. A refusal supersedes
 * nothing: the click never became a run, so only the affordance that can never
 * work again goes away and its siblings stay clickable.
 */
export function removeExcelCheckpointAction(
  messages: readonly ExcelChatMessage[],
  messageId: string,
  actionId: string,
): ExcelChatMessage[] {
  return messages.map((message) =>
    message.id !== messageId || !message.actions
      ? message
      : {
          ...message,
          actions: message.actions.filter((action) => action.id !== actionId),
        },
  );
}

export function invalidateExcelCheckpointActions(
  messages: readonly ExcelChatMessage[],
  consumedAction?: { messageId: string; actionId: string },
): ExcelChatMessage[] {
  return messages.map((message) =>
    !message.actions
      ? message
      : {
          ...message,
          actions: message.actions.filter(
            (action) =>
              !action.pausedPlanPayload &&
              !(
                message.id === consumedAction?.messageId &&
                action.id === consumedAction.actionId
              ),
          ),
        },
  );
}

/**
 * Skill operation ids belonging to continuation actions that
 * `invalidateExcelCheckpointActions` would drop.
 *
 * Separate from the message rewrite so releasing a Skill binding stays outside
 * the React state updater and, more importantly, so the caller can exclude the
 * one operation it is about to continue. Dropping a button means that
 * particular affordance is gone; it does not always mean the operation behind
 * it is over.
 */
export function collectExcelCheckpointContinuationIds(
  messages: readonly ExcelChatMessage[],
  options?: { readonly except?: string },
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const message of messages) {
    for (const action of message.actions ?? []) {
      const id = action.pausedPlanPayload?.skillContinuationId;
      if (!id || id === options?.except || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

export function buildExcelPausedPlanPayload(params: {
  originalQuery: string;
  workbookKey: string;
  workbookName?: string;
  sessionLockSheet: string;
  checkpoint?: ExecutorRunCheckpoint;
  skillContinuationId?: string;
  skillBound?: boolean;
  accountScopeKey?: string | null;
}): ExcelPausedPlanPayload | undefined {
  const checkpoint = params.checkpoint;
  if (
    !checkpoint ||
    checkpoint.plan.steps.length === 0 ||
    !hasActionableExcelCheckpointWork(checkpoint)
  ) {
    return undefined;
  }
  const currentIndex = checkpoint.currentStepId
    ? checkpoint.stepStates.findIndex(
        (step) => step.id === checkpoint.currentStepId,
      )
    : -1;
  const completed = new Set(checkpoint.completedStepIds);
  const firstRemainingIndex = checkpoint.stepStates.findIndex(
    (step) => !completed.has(step.id),
  );
  const blockedStepIndex =
    currentIndex >= 0
      ? currentIndex
      : firstRemainingIndex >= 0
        ? firstRemainingIndex
        : Math.max(0, checkpoint.plan.steps.length - 1);
  const blockedArgs =
    checkpoint.blockedOperation?.args &&
    typeof checkpoint.blockedOperation.args === "object" &&
    !Array.isArray(checkpoint.blockedOperation.args)
      ? checkpoint.blockedOperation.args as Record<string, unknown>
      : undefined;
  const blockedModifications =
    blockedArgs?.modifications &&
    typeof blockedArgs.modifications === "object" &&
    !Array.isArray(blockedArgs.modifications)
      ? blockedArgs.modifications as WorkbookModification
      : undefined;
  return {
    originalQuery: params.originalQuery,
    plan: checkpoint.plan,
    blockedStepIndex,
    ...(blockedModifications ? { blockedModifications } : {}),
    stepResults: checkpoint.stepResults.slice(),
    stepDisplayResults: checkpoint.stepDisplayResults.slice(),
    stepStatuses: checkpoint.stepStatuses.slice(),
    editedCells: checkpoint.editedCells.slice(),
    fdsRefreshSheets: checkpoint.fdsRefreshSheets.slice(),
    deferredRefreshAllRequested:
      checkpoint.deferredRefreshAllRequested,
    deferredRecalcMode: checkpoint.deferredRecalcMode,
    executedFingerprints: checkpoint.executedWriteFingerprints.slice(),
    sessionLockSheet: params.sessionLockSheet,
    workbookKey: params.workbookKey,
    ...(params.workbookName ? { workbookName: params.workbookName } : {}),
    ...(checkpoint.lastModifications
      ? { lastModifications: checkpoint.lastModifications }
      : {}),
    ...(params.skillContinuationId
      ? { skillContinuationId: params.skillContinuationId }
      : {}),
    ...(params.skillBound ? { skillBound: true } : {}),
    // Written even when null, so a payload from an unauthenticated pane is
    // still distinguishable from one that predates the field.
    ...(params.accountScopeKey !== undefined
      ? { accountScopeKey: params.accountScopeKey }
      : {}),
    checkpoint,
  };
}

/**
 * A warning or partial summary is not necessarily resumable. Continuation is
 * useful only when the checkpoint still contains an incomplete step, a blocked
 * write, or deterministic verification debt that another run can reduce.
 */
export function isReadbackRecoverableBlockedWrite(toolName: string): boolean {
  return toolName === "set_range_values" || toolName === "set_range_formulas";
}

export function hasActionableExcelCheckpointWork(
  checkpoint: ExecutorRunCheckpoint | undefined,
): boolean {
  if (!checkpoint || checkpoint.plan.steps.length === 0) return false;
  const tabularWorkload = checkpoint.tabularQueryWorkload?.workload;
  if (
    tabularWorkload &&
    (tabularWorkload.waveInterrupted ||
      tabularWorkload.activeWaveRows.length > 0 ||
      tabularWorkload.rows.some((row) => row.status !== "closed"))
  ) {
    return true;
  }
  const completed = new Set(checkpoint.completedStepIds);
  // A warning is already a terminal observed outcome. It should not offer a
  // continuation by itself; concrete refresh/readback debt below may still do so.
  if (
    checkpoint.stepStates.some(
      (step) => !completed.has(step.id) && step.status !== "warning",
    )
  ) {
    return true;
  }
  if (
    checkpoint.blockedOperation &&
    isReadbackRecoverableBlockedWrite(checkpoint.blockedOperation.toolName)
  ) {
    return true;
  }

  const verification = checkpoint.verification;
  const verifiedReadbackSheets = new Set(
    verification.verifiedReadbackSheetNames.map((sheetName) =>
      sheetName.trim().toLowerCase()
    ),
  );
  const verifiedReadbackTargets = new Set(
    verification.verifiedReadbackTargets.map(
      (target) => `${target.sheetName.toLowerCase()}!${normalizeAddress(target.address)}`,
    ),
  );
  const pendingReadbackRemains =
    verification.pendingReadbackSheetNames.some(
      (sheetName) =>
        !verifiedReadbackSheets.has(sheetName.trim().toLowerCase()),
    ) ||
    verification.pendingReadbackTargets.some(
      (target) =>
        !verifiedReadbackTargets.has(
          `${target.sheetName.toLowerCase()}!${normalizeAddress(target.address)}`,
        ),
    );
  return (
    checkpoint.fdsRefreshSheets.length > 0 ||
    checkpoint.deferredRefreshAllRequested ||
    checkpoint.deferredRecalcMode !== null ||
    verification.refreshRequired ||
    verification.recalcRequired ||
    verification.hardErrors.length > 0 ||
    verification.pendingShapeCorrections.length > 0 ||
    verification.pendingContentReadbackTargets.length > 0 ||
    verification.contentReadbackFailures.length > 0 ||
    pendingReadbackRemains ||
    (verification.requiresVerification &&
      !verification.cleanReadbackAfterRecalc)
  );
}

function normalizeAddress(address: string): string {
  const local = address.includes("!")
    ? address.slice(address.lastIndexOf("!") + 1)
    : address;
  return local.replace(/\$/g, "").trim().toUpperCase();
}

export function reconcileCheckpointAfterFinalize(
  checkpoint: ExecutorRunCheckpoint | undefined,
  result: {
    refreshCompleted: boolean;
    refreshedSheetNames?: readonly string[];
    refreshAllCompleted?: boolean;
    recalcCompleted: boolean;
  },
): ExecutorRunCheckpoint | undefined {
  if (!checkpoint) return undefined;
  const refreshed = new Set(
    (result.refreshedSheetNames ?? []).map((name) =>
      name.trim().toLowerCase()
    ),
  );
  const fdsRefreshSheets = result.refreshCompleted
    ? result.refreshAllCompleted
      ? []
      : checkpoint.fdsRefreshSheets.filter(
          (name) => !refreshed.has(name.trim().toLowerCase()),
        )
    : checkpoint.fdsRefreshSheets.slice();
  const deferredRefreshAllRequested =
    result.refreshCompleted && result.refreshAllCompleted
      ? false
      : checkpoint.deferredRefreshAllRequested;
  const refreshDebt =
    fdsRefreshSheets.length > 0 || deferredRefreshAllRequested;
  const deferredRecalcMode = result.recalcCompleted
    ? null
    : checkpoint.deferredRecalcMode;
  const recalcDebt =
    refreshDebt ||
    (!result.recalcCompleted &&
      (deferredRecalcMode !== null ||
        checkpoint.verification.recalcRequired ||
        result.refreshCompleted));

  return {
    ...checkpoint,
    fdsRefreshSheets,
    deferredRefreshAllRequested,
    deferredRecalcMode,
    verification: {
      ...checkpoint.verification,
      refreshObserved:
        checkpoint.verification.refreshObserved || result.refreshCompleted,
      refreshRequired: refreshDebt,
      recalcObserved:
        checkpoint.verification.recalcObserved || result.recalcCompleted,
      recalcRequired: recalcDebt,
      cleanReadbackAfterRecalc: result.recalcCompleted
        ? false
        : checkpoint.verification.cleanReadbackAfterRecalc,
    },
  };
}

export function reconcileCheckpointAfterFormulaScan(
  checkpoint: ExecutorRunCheckpoint | undefined,
  result: {
    completed: boolean;
    errors: readonly ExcelHardErrorCell[];
  },
): ExecutorRunCheckpoint | undefined {
  if (!checkpoint || !result.completed) return checkpoint;
  const clean = result.errors.length === 0;
  return {
    ...checkpoint,
    verification: {
      ...checkpoint.verification,
      hardErrors: result.errors.map((error) => ({ ...error })),
      cleanReadbackAfterRecalc: clean,
      verifiedReadbackSheetNames: clean
        ? checkpoint.verification.pendingReadbackSheetNames.slice()
        : checkpoint.verification.verifiedReadbackSheetNames.slice(),
      verifiedReadbackTargets: clean
        ? checkpoint.verification.pendingReadbackTargets.map((target) => ({
            ...target,
          }))
        : checkpoint.verification.verifiedReadbackTargets.map((target) => ({
            ...target,
          })),
    },
  };
}

export function synchronizeCheckpointStepOutcomes(
  checkpoint: ExecutorRunCheckpoint | undefined,
  outcome: {
    stepResults: readonly string[];
    stepDisplayResults: readonly string[];
    stepStatuses: readonly ExcelStepStatus[];
  },
): ExecutorRunCheckpoint | undefined {
  if (!checkpoint) return undefined;
  const stepStates = checkpoint.stepStates.map((step, index) => ({
    ...step,
    planStep: { ...step.planStep },
    status: outcome.stepStatuses[index] ?? step.status,
  }));
  // The checkpoint's completed IDs are the authoritative pre-finalize set. A
  // run-level concern can demote one of those steps to "warning", and a
  // completed row-level workload can intentionally finish with a warning.
  // Warnings absent from this set remain available if deterministic debt below
  // makes continuation actionable.
  const completedBeforeFinalize = new Set(checkpoint.completedStepIds);
  const stepResults = outcome.stepResults.slice();
  return {
    ...checkpoint,
    stepStates,
    completedStepIds: stepStates
      .filter(
        (step) =>
          step.status === "success" ||
          step.status === "recovered" ||
          (step.status === "warning" && completedBeforeFinalize.has(step.id)),
      )
      .map((step) => step.id),
    terminalStepIds: stepStates
      .filter(
        (step) =>
          step.status !== "queued" &&
          step.status !== "running" &&
          step.status !== "verifying",
      )
      .map((step) => step.id),
    stepResults,
    // Rebuild the id→index mapping from the post-finalize `stepResults` so it
    // never carries the pre-finalize indices forward stale (post-finalize
    // warning edits can rewrite result lines). Match each step to its line by
    // the stable `${label}.` prefix that every result line is written with.
    stepResultIndexById: rebuildStepResultIndexById(stepStates, stepResults),
    stepDisplayResults: outcome.stepDisplayResults.slice(),
    stepStatuses: outcome.stepStatuses.slice(),
  };
}

/**
 * Re-derives the `stepResultIndexById` entries so they point at the correct
 * lines in `stepResults`. Result lines are always prefixed with the step's
 * numeric label (`${label}.`); each line is claimed by at most one step.
 */
function rebuildStepResultIndexById(
  stepStates: ReadonlyArray<{ id: string; planStep: { step: number } }>,
  stepResults: readonly string[],
): Array<[string, number]> {
  const mapping: Array<[string, number]> = [];
  const usedLineIndexes = new Set<number>();
  stepStates.forEach((step, index) => {
    const label = step.planStep.step || index + 1;
    const prefix = `${label}.`;
    const resultIndex = stepResults.findIndex(
      (line, lineIndex) =>
        !usedLineIndexes.has(lineIndex) && line.trimStart().startsWith(prefix),
    );
    if (resultIndex >= 0) {
      usedLineIndexes.add(resultIndex);
      mapping.push([step.id, resultIndex]);
    }
  });
  return mapping;
}
